// frontend/src/components/MaterialProducer.js

import React, { useState, useEffect, useRef } from 'react';
import styles from './MaterialProducer.module.css';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { getLibraries } from '../api';
import MaterialResultModal from './MaterialResultModal';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// AJUSTE FINAL: Apontar para o worker na pasta /public.
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';

const ACCEPTED_FILE_TYPES = ".pdf,.pptx,.txt,.mp4,.webm,.mkv,.mp3,.wav,.ogg,.png,.jpg,.jpeg,.gif";

function MaterialProducer({ isSidebarOpen }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [libraries, setLibraries] = useState([]);
  
  const [result, setResult] = useState(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  
  // AJUSTE 1: Adicionar estado para o número de páginas do PDF
  const [numPages, setNumPages] = useState(null);

  useEffect(() => {
    const updatePreviewWidth = () => {
      if (previewContainerRef.current) {
        setPreviewWidth(previewContainerRef.current.offsetWidth);
      }
    };
    updatePreviewWidth();
    window.addEventListener('resize', updatePreviewWidth);
    return () => {
      window.removeEventListener('resize', updatePreviewWidth);
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setFilePreviewUrl(objectUrl);

    const fileType = file.type;
    const isPreviewable = fileType.startsWith('video/') || fileType.startsWith('image/') || fileType.startsWith('audio/') || fileType === 'application/pdf' || fileType === 'text/plain';
    const isProcessable = ACCEPTED_FILE_TYPES.includes(`.${file.name.split('.').pop()}`);

    if (isProcessable && !isPreviewable) {
      addNotification(t('previewNotAvailableButProcessable'), 'info');
    }

    return () => URL.revokeObjectURL(objectUrl);
  }, [file, addNotification, t]);

  useEffect(() => {
    const fetchLibraries = async () => {
      try {
        const userLibraries = await getLibraries();
        setLibraries(userLibraries);
      } catch (error) {
        console.error("Failed to fetch libraries:", error);
        addNotification(t('errorLoadingLibraries'), 'error');
      }
    };
    fetchLibraries();
  }, [addNotification, t]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileExtension = `.${selectedFile.name.split('.').pop().toLowerCase()}`;
    if (!ACCEPTED_FILE_TYPES.includes(fileExtension)) {
      addNotification(t('fileTypeNotSupported'), 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setFileContent(null);
    setResult(null);
    setFile(selectedFile);
    setSelectedLibrary(null);
    setNumPages(null); // Resetar o número de páginas ao trocar de arquivo

    if (selectedFile.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => setFileContent(event.target.result);
      reader.readAsText(selectedFile);
    }
  };

  const handleLibraryChange = (e) => {
    const libraryId = e.target.value;
    if (libraryId) {
        const library = libraries.find(lib => lib.id === parseInt(libraryId));
        setSelectedLibrary(library);
        setFile(null);
        setFileContent(null);
        setNumPages(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
        setSelectedLibrary(null);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileContent(null);
    setNumPages(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerateMaterial = async (materialType) => {
    if (!file && !selectedLibrary) {
      addNotification(t('noFileOrLibrarySelected'), 'warning');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      let payload = { material_type: materialType };
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        const uploadData = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadData.error || t('uploadError'));
        payload.source_type = 'filepath';
        payload.source_value = uploadData.filepath;
      } else if (selectedLibrary) {
        payload.source_type = 'library_id';
        payload.source_value = selectedLibrary.id;
      }
      const processResponse = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      const processData = await processResponse.json();
      if (!processResponse.ok) throw new Error(processData.error || t('processingError'));
      setResult(processData);
      setIsResultModalOpen(true);
      addNotification(t('materialGeneratedSuccess'), 'success');
    } catch (error) {
      console.error('Erro ao gerar material:', error);
      addNotification(`${t('error')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const materialTypes = [
    { id: 'summary', title: t('summary'), description: t('summaryDescription') },
    { id: 'detailed_text', title: t('detailedText'), description: t('detailedTextDescription') },
    { id: 'flashcards', title: t('flashcards'), description: t('flashcardsDescription') },
    { id: 'mind_map', title: t('mindMap'), description: t('mindMapDescription') },
    { id: 'questionnaire', title: t('questionnaire'), description: t('questionnaireDescription') },
    { id: 'podcast', title: t('podcast'), description: t('podcastDescription') },
    { id: 'slideshow', title: t('slideshow'), description: t('slideshowDescription') },
  ];

  // AJUSTE 2: Função para atualizar o estado com o número de páginas
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const renderPreview = () => {
    if (!file) return null;
    const fileType = file.type;

    if (fileType.startsWith('video/')) {
      return <video src={filePreviewUrl} controls className={styles.mediaPreview} />;
    }
    if (fileType.startsWith('image/')) {
      return <img src={filePreviewUrl} alt={file.name} className={styles.mediaPreview} />;
    }
    if (fileType.startsWith('audio/')) {
      return <audio src={filePreviewUrl} controls className={styles.audioPreview} />;
    }
    if (fileType === 'application/pdf') {
      return (
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess} // Usar a nova função
          onLoadError={console.error}
          loading={t('loadingPdf')}
        >
          {/* AJUSTE 3: Mapear e renderizar todas as páginas */}
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={previewWidth}
              className={styles.pdfPage}
            />
          ))}
        </Document>
      );
    }
    if (fileType === 'text/plain' && fileContent) {
      return <pre className={styles.textPreview}>{fileContent}</pre>;
    }
    return <p className={styles.previewPlaceholder}>{t('previewNotAvailable')}</p>;
  };

  return (
    <div className={`${styles.materialProducer} ${!isSidebarOpen ? styles.adjustedForClosedSidebar : ''}`}>
      <div className={styles.mainContainer}>
        <div className={styles.leftPanel}>
          <div className={styles.sourceSelectionContainer}>
            <div className={`${styles.fileContainer} ${file ? styles.fileSelected : ''}`}>
              <button className={styles.fileUploadButton} onClick={() => fileInputRef.current?.click()}>
                {t('chooseFile')}
              </button>
              <input
                id="fileInput"
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept={ACCEPTED_FILE_TYPES}
                style={{ display: 'none' }}
              />
              {file && (
                <>
                  <span className={styles.fileName} title={file.name}>{file.name}</span>
                  <button onClick={handleRemoveFile} className={styles.removeFileButton} title={t('removeFile')}>×</button>
                </>
              )}
            </div>
            <div className={styles.orSeparator}>
                <span>{t('or')}</span>
            </div>
            <select value={selectedLibrary?.id || ''} onChange={handleLibraryChange} className={styles.themeSelect} disabled={libraries.length === 0}>
                <option value="">{t('selectLibraryAsBase')}</option>
                {libraries.map(lib => (<option key={lib.id} value={lib.id}>{lib.name}</option>))}
            </select>
          </div>
          <div className={styles.materialGrid}>
            {materialTypes.map(material => (
              <div key={material.id} className={styles.materialCard} onClick={() => handleGenerateMaterial(material.id)}>
                <h3 className={styles.cardTitle}>{material.title}</h3>
                <p className={styles.cardDescription}>{material.description}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.rightPanel}>
            <div className={styles.previewWrapper}>
              <div className={styles.previewContent}>
                {file ? renderPreview() : (
                  selectedLibrary ? (
                    <div className={styles.previewPlaceholder}>
                      <p>{t('librarySelected')}: <strong>{selectedLibrary.name}</strong></p>
                      <span>{t('libraryPreviewNotAvailable')}</span>
                    </div>
                  ) : (
                    <div className={styles.previewPlaceholder}>
                      <p>{t('selectSourceForPreview')}</p>
                    </div>
                  )
                )}
              </div>
            </div>
        </div>
      </div>
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.ecgAnimation}>
            <svg viewBox="0 0 100 25" preserveAspectRatio="none">
              <path id="ecgPath" className={styles.ecgLine} d="M0,20 L10,20 L12,10 L14,30 L16,20 L18,20 L20,20 L22,10 L24,30 L26,20 L28,20 L30,20 L32,10 L34,30 L36,20 L38,20 L40,20 L42,10 L44,30 L46,20 L48,20 L50,20 L52,10 L54,30 L56,20 L58,20 L60,20 L62,10 L64,30 L66,20 L68,20 L70,20 L72,10 L74,30 L76,20 L78,20 L80,20 L82,10 L84,30 L86,20 L88,20 L90,20 L92,10 L94,30 L96,20 L98,20 L100,20" />
              <path className={styles.ecgTrail} d="M0,20 L10,20 L12,10 L14,30 L16,20 L18,20 L20,20 L22,10 L24,30 L26,20 L28,20 L30,20 L32,10 L34,30 L36,20 L38,20 L40,20 L42,10 L44,30 L46,20 L48,20 L50,20 L52,10 L54,30 L56,20 L58,20 L60,20 L62,10 L64,30 L66,20 L68,20 L70,20 L72,10 L74,30 L76,20 L78,20 L80,20 L82,10 L84,30 L86,20 L88,20 L90,20 L92,10 L94,30 L96,20 L98,20 L100,20" />
              <circle cx="0" cy="0" r="2" className={styles.ecgDot}><animateMotion dur="2s" repeatCount="indefinite" rotate="auto"><mpath href="#ecgPath" /></animateMotion></circle>
            </svg>
            <p>{t('generatingMaterial')}</p>
          </div>
        </div>
      )}
      <MaterialResultModal
        isOpen={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
        result={result}
        sourceName={file?.name || selectedLibrary?.name}
        sourceType={file ? t('file') : (selectedLibrary ? t('library') : '')}
      />
    </div>
  );
}

export default MaterialProducer;